// Mapbox utilities for geocoding and directions

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * Geocode an address to coordinates
 * @param {string} address - The address to geocode
 * @returns {Promise<{lat: number, lng: number} | null>}
 */
export async function geocodeAddress(address) {
  if (!address || !MAPBOX_TOKEN) return null;
  
  try {
    const encodedAddress = encodeURIComponent(address);
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${MAPBOX_TOKEN}&limit=1`
    );
    
    if (!response.ok) {
      console.error('Geocoding failed:', response.status);
      return null;
    }
    
    const data = await response.json();
    
    if (data.features && data.features.length > 0) {
      const [lng, lat] = data.features[0].center;
      return { lat, lng };
    }
    
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

/**
 * Get driving directions and ETA between two points
 * @param {Object} origin - {lat, lng}
 * @param {Object} destination - {lat, lng}
 * @returns {Promise<{duration: number, distance: number, durationText: string, distanceText: string} | null>}
 */
export async function getDirections(origin, destination) {
  if (!origin || !destination || !MAPBOX_TOKEN) return null;
  
  try {
    const coordinates = `${origin.lng},${origin.lat};${destination.lng},${destination.lat}`;
    const response = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/driving-traffic/${coordinates}?access_token=${MAPBOX_TOKEN}&overview=false`
    );
    
    if (!response.ok) {
      console.error('Directions failed:', response.status);
      return null;
    }
    
    const data = await response.json();
    
    if (data.routes && data.routes.length > 0) {
      const route = data.routes[0];
      const durationSeconds = route.duration; // Duration in seconds
      const distanceMeters = route.distance; // Distance in meters

      // Feb 2026 — use Math.round (vs ceil) to match Apple Maps / Google
      // Maps "leaving now" rounding behaviour. The previous Math.ceil
      // added up to 59 extra seconds to every ETA, which is what made
      // ours feel ~1min slower than the driver's actual Apple Maps ETA.
      // For sub-minute durations we still floor to "Less than 1 min" so
      // we never show "0 min".
      const minutes = Math.round(durationSeconds / 60);
      let durationText;
      if (minutes < 1) {
        durationText = 'Less than 1 min';
      } else if (minutes === 1) {
        durationText = '1 min';
      } else {
        durationText = `${minutes} min`;
      }
      
      // Format distance
      const miles = distanceMeters / 1609.34;
      let distanceText;
      if (miles < 0.1) {
        distanceText = `${Math.round(distanceMeters)} m`;
      } else {
        distanceText = `${Number(miles ?? 0).toFixed(1)} mi`;
      }
      
      return {
        duration: durationSeconds,
        distance: distanceMeters,
        durationMinutes: minutes,
        durationText,
        distanceText
      };
    }
    
    return null;
  } catch (error) {
    console.error('Directions error:', error);
    return null;
  }
}

/**
 * Calculate ETA with caching for the destination geocoding
 */
const geocodeCache = new Map();

export async function getETAToAddress(driverLocation, deliveryAddress) {
  if (!driverLocation || !deliveryAddress) return null;
  
  // Check cache for geocoded address
  let destinationCoords = geocodeCache.get(deliveryAddress);
  
  if (!destinationCoords) {
    destinationCoords = await geocodeAddress(deliveryAddress);
    if (destinationCoords) {
      geocodeCache.set(deliveryAddress, destinationCoords);
    }
  }
  
  if (!destinationCoords) {
    console.error('Could not geocode delivery address');
    return null;
  }
  
  // Get directions
  const directions = await getDirections(
    { lat: driverLocation.lat, lng: driverLocation.lng },
    destinationCoords
  );
  
  return directions;
}

export default {
  geocodeAddress,
  getDirections,
  getETAToAddress
};

/**
 * Haversine distance in miles between two {lat,lng} points.
 */
export function haversineMiles(a, b) {
  if (!a || !b) return null;
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 3958.8; // Earth radius in miles
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
